import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { formatTimestampInTimezone } from "@alm/domain";
import { Plus } from "@/lib/icons";
import { useState } from "react";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { useZone } from "@/lib/facility/zone";
import { SectionEmpty } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/section-empty";
import { AddActivityDialog } from "@/routes/_authenticated/_care/residents/$residentId/-components/stay/add-activity-dialog";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/activities")({
  component: ResidentActivitiesTab,
});

/** What this resident has joined in with, newest first. */
function ResidentActivitiesTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canWrite = useCan("activity:manage", resident.facilityId);
  const zone = useZone(resident.facilityId);
  const [adding, setAdding] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const activities = useQuery(
    orpc.care.activities.list.queryOptions({ input: { residentId, limit: 100 } }),
  );
  const rows = activities.data?.rows ?? [];
  const shown = showAll ? rows : rows.slice(0, 10);
  const addButton = (variant: "default" | "outline") =>
    canWrite ? (
      <Button variant={variant} onClick={() => setAdding(true)}>
        {variant === "default" ? <Plus className="size-4" /> : null}
        Add activity
      </Button>
    ) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Recent activities</h2>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {rows.length > 0
              ? `${shown.length} of ${rows.length} shown, newest first`
              : "Activity log for this resident"}
          </p>
        </div>
        {addButton("default")}
      </div>
      {activities.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : activities.isError ? (
        <ReadFailed
          message="Could not load this resident's activities."
          error={activities.error}
          onRetry={() => void activities.refetch()}
          retrying={activities.isFetching}
        />
      ) : rows.length === 0 ? (
        <Card>
          <SectionEmpty
            title="No activities logged"
            detail="Meals, exercise, social time and therapy appear here once staff record them."
            action={addButton("outline")}
          />
        </Card>
      ) : (
        <Card className="gap-0 py-0">
          {shown.map((row) => (
            <div
              key={row.id}
              className="border-border-soft flex items-start gap-3 border-b px-[18px] py-4 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm">{row.description}</p>
                <p className="text-muted-foreground mt-1.5 text-xs tabular-nums">
                  {formatTimestampInTimezone(row.performedAt, zone)}
                </p>
              </div>
              <Badge variant="secondary" className="shrink-0 capitalize">
                {row.activityType.replaceAll("_", " ")}
              </Badge>
            </div>
          ))}
          {rows.length > shown.length ? (
            <div className="flex justify-center p-3">
              <Button variant="ghost" onClick={() => setShowAll(true)}>
                Show all {rows.length} activities
              </Button>
            </div>
          ) : null}
        </Card>
      )}
      {adding ? (
        <AddActivityDialog residentId={residentId} onClose={() => setAdding(false)} />
      ) : null}
    </div>
  );
}
